import { companyReportUrls } from '@app/ccf/company-report/report/company-report-utils';
import EmissionsByCategory from '@app/components/reports/sections/food-and-drink/emissions-by-category';
import FoodAndDrink from '@app/components/reports/sections/food-and-drink/food-and-drink';
import FoodItems from '@app/components/reports/sections/food-and-drink/food-items';
import EmissionsByMaterial from '@app/components/reports/sections/packaging/emissions-by-material';
import Packaging from '@app/components/reports/sections/packaging/packaging';
import PackagingItems from '@app/components/reports/sections/packaging/packaging-items';
import EmissionsByCustomer from '@app/components/reports/sections/result-overview/emissions-by-customer';
import EmissionsBySite from '@app/components/reports/sections/result-overview/emissions-by-site';
import EmissionsByTag from '@app/components/reports/sections/result-overview/emissions-by-tag';
import EmissionsOverTime from '@app/components/reports/sections/result-overview/emissions-over-time';
import ResultsOverview from '@app/components/reports/sections/result-overview/results-overview';
import { ReportSection } from '@app/components/reports/types';
import {
  ReportFilters,
  scrollTo,
  useReportFilterParams,
} from '@app/components/reports/utils';
import {
  OperationReportTypeEnum,
  SalesReportResultData,
} from '@shared/api/types';
import { generatePath } from 'react-router-dom';
import { salesReportSlugs } from '../slugs';
import { soldGoodsRoutes } from '../sold-goods-routes';
import { salesReportUrls } from './sales-report-utils';

export function useSalesReportSections(
  result: SalesReportResultData,
  reportUuid?: string
): ReportSection[] {
  const filterParams = useReportFilterParams(
    result?.filters as ReportFilters[]
  );
  if (!reportUuid || !result) return [];

  // Append url params to sold items links
  const soldFoodItemsLink = `${generatePath(
    soldGoodsRoutes.SOLD_FOOD_AND_DRINK
  )}?${filterParams.toString()}`;

  const soldPackagingItemsLink = `${generatePath(
    soldGoodsRoutes.SOLD_PACKAGING
  )}?${filterParams.toString()}`;

  return [
    {
      title: 'Results overview',
      preTitle: '',
      slug: salesReportSlugs.resultOverview,
      url: salesReportUrls.resultOverview,
      onClick: () => scrollTo(companyReportUrls.introduction),
      component: <ResultsOverview reportType={OperationReportTypeEnum.sales} />,
      show: true,
      items: [
        {
          title: 'Emissions over time',
          preTitle: 'Results overview',
          slug: salesReportSlugs.emissionsOverTime,
          url: salesReportUrls.emissionsOverTime,
          onClick: () => scrollTo(salesReportUrls.emissionsOverTime),
          component: (
            <EmissionsOverTime reportType={OperationReportTypeEnum.sales} />
          ),
          show: true,
        },
        {
          title: 'Emissions by site',
          preTitle: 'Results overview',
          slug: salesReportSlugs.emissionsBySite,
          url: salesReportUrls.emissionsBySite,
          onClick: () => scrollTo(salesReportUrls.emissionsBySite),
          component: (
            <EmissionsBySite reportType={OperationReportTypeEnum.sales} />
          ),
          show: true,
        },
        {
          title: 'Emissions by customer',
          preTitle: 'Results overview',
          slug: salesReportSlugs.emissionsByCustomer,
          url: salesReportUrls.emissionsByCustomer,
          onClick: () => scrollTo(salesReportUrls.emissionsByCustomer),
          component: (
            <EmissionsByCustomer reportType={OperationReportTypeEnum.sales} />
          ),
          show: true,
        },
        {
          title: 'Emissions by tag',
          preTitle: 'Results overview',
          slug: salesReportSlugs.emissionsByTag,
          url: salesReportUrls.emissionsByTag,
          onClick: () => scrollTo(salesReportUrls.emissionsByTag),
          component: (
            <EmissionsByTag reportType={OperationReportTypeEnum.sales} />
          ),
          show: true,
        },
      ],
    },
    {
      title: 'Food and drink',
      preTitle: '',
      url: salesReportUrls.foodAndDrink,
      slug: salesReportSlugs.foodAndDrink,
      onClick: () => scrollTo(salesReportUrls.foodAndDrink),
      component: <FoodAndDrink reportType={OperationReportTypeEnum.sales} />,
      show: true,
      items: [
        {
          title: 'Emissions by category',
          preTitle: 'Food and drink',
          slug: salesReportSlugs.emissionsByCategory,
          url: salesReportUrls.emissionsByCategory,
          onClick: () => scrollTo(salesReportUrls.emissionsByCategory),
          component: (
            <EmissionsByCategory reportType={OperationReportTypeEnum.sales} />
          ),
          show: true,
        },
        {
          title: 'Sold items',
          preTitle: 'Food and drink',
          slug: salesReportSlugs.soldItemsFood,
          url: salesReportUrls.soldItemsFood,
          onClick: () => scrollTo(salesReportUrls.soldItemsFood),
          component: <FoodItems reportType={OperationReportTypeEnum.sales} />,
          show: true,
          link: {
            title: 'View all items',
            linkTo: generatePath(soldFoodItemsLink),
          },
        },
      ],
    },
    {
      title: 'Packaging',
      preTitle: '',
      url: salesReportUrls.packaging,
      slug: salesReportSlugs.packaging,
      onClick: () => scrollTo(salesReportUrls.packaging),
      component: <Packaging reportType={OperationReportTypeEnum.sales} />,
      show: true,
      items: [
        {
          title: 'Emissions by material',
          preTitle: 'Packaging',
          url: salesReportUrls.emissionsByMaterial,
          slug: salesReportSlugs.emissionsByMaterial,
          onClick: () => scrollTo(salesReportUrls.emissionsByMaterial),
          component: (
            <EmissionsByMaterial reportType={OperationReportTypeEnum.sales} />
          ),
          show: true,
        },
        {
          title: 'Sold items',
          preTitle: 'Packaging',
          url: salesReportUrls.soldItemsPackaging,
          slug: salesReportSlugs.soldItemsPackaging,
          onClick: () => scrollTo(salesReportUrls.soldItemsPackaging),
          component: (
            <PackagingItems reportType={OperationReportTypeEnum.sales} />
          ),
          show: true,
          link: {
            title: 'View all items',
            linkTo: generatePath(soldPackagingItemsLink),
          },
        },
      ],
    },
  ];
}
